import { FC, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useRouter } from 'shared-routing';
import type { RecommendationItem } from 'voyager';
import { BasketDrawerTemplate } from 'voyager';
import type { BasketDrawerView } from 'voyager';
import { paymentOptions, Routes } from '@common/constants';
import { useTranslations } from '@lang';
import { analytics, formatBasketItems, formatPrice } from '@features/analytics';
import { createOrder } from '@store/basket/actions';
import { basketSelectors } from '@store/basket/selectors';
import { entitySelectors } from '@store/entity/selectors';
import { ReduxPageType } from '@common-types/ReduxPage';
import { useLabels } from '@features/Menu/useLabels';
import { getItineraryItems } from '@utils/getItineraryView';
import { calculateTotalNumberOfItemsInBasket, mapModules } from '../BasketDrawer.helpers';
import { ModuleType } from '../BasketListing.types';
import { useModulesToRecommend } from '../hooks/useModulesToRecommend';
import { toBasketCardItems } from './adapters/toBasketCardItem';
import { toItineraryGuests } from './adapters/toItineraryGuests';

type BasketDrawerV2ConnectedProps = {
  open: boolean;
  onClose: () => void;
  propertyModules: string[];
};

export const BasketDrawerV2Connected: FC<BasketDrawerV2ConnectedProps> = ({
  open,
  onClose,
  propertyModules,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const router = useRouter();
  const dispatch = useDispatch();
  const isSinglePageCheckout = useFeatureIsOn('feature.single-page-checkout');
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as any;
  const spaPage = useSelector(entitySelectors.getSpaPage) as ReduxPageType;
  const tablesPage = useSelector(entitySelectors.getTablesPage) as ReduxPageType;
  const roomsPage = useSelector((state: any) => entitySelectors.getPage(state, 'rooms')) as ReduxPageType;
  const [view, setView] = useState<BasketDrawerView>('itemised');
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  const guestItineraries = getItineraryItems(basketDetails?.items ?? [], labels);

  const items = useMemo(
    () =>
      toBasketCardItems(basketDetails?.items ?? [], {
        t,
        currencyCode: basketDetails?.total?.currencyCode ?? '',
        moduleImages: {
          rooms: roomsPage?.heroBackgroundImage?.url,
          spa: spaPage?.heroBackgroundImage?.url,
          tables: tablesPage?.heroBackgroundImage?.url,
        },
      }),
    [
      basketDetails?.items,
      basketDetails?.total?.currencyCode,
      t,
      roomsPage?.heroBackgroundImage?.url,
      spaPage?.heroBackgroundImage?.url,
      tablesPage?.heroBackgroundImage?.url,
    ],
  );

  const guests = useMemo(() => toItineraryGuests(guestItineraries), [guestItineraries]);

  const recommendedModules = useModulesToRecommend({
    modules: propertyModules as ModuleType[],
    items: basketDetails?.items ?? [],
  });

  const { recommendations, routeById } = useMemo(() => {
    const pages = mapModules(recommendedModules, { spaPage, tablesPage, roomsPage }).filter(
      (page): page is NonNullable<typeof page> => page != null,
    );
    const recs: RecommendationItem[] = pages.map((page) => ({
      id: String(page.id),
      name: page.title,
      imageUrl: page.heroBackgroundImage?.url,
    }));
    const map = new Map<string, string>(pages.map((page) => [String(page.id), page.route]));
    return { recommendations: recs, routeById: map };
  }, [recommendedModules, spaPage, tablesPage, roomsPage]);

  const handleCheckout = useCallback(async () => {
    if (isStartingCheckout || !basketDetails) return;

    analytics.track({
      event: 'begin_checkout',
      params: {
        currency: basketDetails.total?.currencyCode,
        value: formatPrice(basketDetails.total?.amount),
        items: formatBasketItems(basketDetails.items || []),
        coupon: basketDetails.promoCode || undefined,
      },
    });

    if (!isSinglePageCheckout) {
      router.push({ pathname: Routes.CHECKOUT });
      return;
    }

    const hasExpiredItems = basketDetails.items?.some(
      (item: { expireAt?: string }) =>
        item.expireAt && Date.now() > Date.parse(item.expireAt)
    );
    if (hasExpiredItems) {
      router.push({ pathname: Routes.BASKET });
      return;
    }

    setIsStartingCheckout(true);
    try {
      const result = (await (dispatch as (action: unknown) => Promise<unknown>)(
        createOrder({
          basket: { id: basketDetails.id },
          agreedToMarketingEmail: false,
          agreedToMarketingSms: false,
          paymentOption: basketDetails.fullPaymentAllowed
            ? paymentOptions.FULL
            : paymentOptions.DEFERRED,
          // Opt out of auto-complete so the user lands on FlatCheckout's
          // acknowledgement step rather than being whisked straight to
          // /confirmation when vouchers already cover the basket total.
          skipCompletion: true,
        })
      )) as { orderReference?: string } | undefined;

      if (result?.orderReference) {
        router.push({ pathname: Routes.CHECKOUT });
      } else {
        router.push({ pathname: Routes.BASKET });
      }
    } finally {
      setIsStartingCheckout(false);
    }
  }, [isStartingCheckout, basketDetails, isSinglePageCheckout, dispatch, router]);

  if (!open || !basketDetails) return null;

  return (
    <BasketDrawerTemplate
      open={open}
      onClose={onClose}
      items={items}
      totalPrice={{
        amount: basketDetails.total.amount,
        currencyCode: basketDetails.total.currencyCode,
      }}
      itemCount={calculateTotalNumberOfItemsInBasket(basketDetails.items)}
      view={view}
      onViewChange={setView}
      guests={guests}
      recommendations={recommendations}
      onRecommendationSelect={(id) => {
        const route = routeById.get(id);
        if (route) router.push({ pathname: route });
      }}
      onViewBasket={() => router.push({ pathname: Routes.BASKET })}
      onCheckout={handleCheckout}
      labels={{
        title: t('basketDrawer.title'),
        itemisedView: t('basket.itemisedView'),
        itineraryView: t('basket.itineraryView'),
        total: t('basketDrawer.total'),
        viewBasket: t('basketDrawer.viewBasket'),
        checkout: t('basketDrawer.checkout'),
      }}
    />
  );
};
